/**
 * Popular filters controller for the collections catalog page.
 * Opens the theme filter drawer and refreshes the Effilo product grid
 * in place using a filtered collection alternate template.
 */
(function () {
  var FILTER_VIEW = 'effilo-filter-grid';
  var FILTER_PARAM_PREFIX = 'filter.';

  /**
   * Returns whether a search param key is a storefront filter param.
   * @param {string} key
   * @returns {boolean}
   */
  function isFilterParam(key) {
    return key === 'sort_by' || key.indexOf(FILTER_PARAM_PREFIX) === 0;
  }

  /**
   * Builds a URLSearchParams of only filter/sort params from a source.
   * @param {URLSearchParams|FormData|string} source
   * @returns {URLSearchParams}
   */
  function pickFilterParams(source) {
    var input =
      typeof source === 'string'
        ? new URLSearchParams(source)
        : source instanceof URLSearchParams
          ? source
          : new URLSearchParams(source);
    var output = new URLSearchParams();

    input.forEach(function (value, key) {
      if (!isFilterParam(key)) return;
      if (value === '' || value == null) return;
      output.append(key, value);
    });

    output.delete('price_range');
    return output;
  }

  /**
   * Reads active filter params from the current page URL.
   * @returns {URLSearchParams}
   */
  function getPageFilterParams() {
    return pickFilterParams(window.location.search);
  }

  /**
   * Updates the browser URL while staying on the catalog page.
   * @param {URLSearchParams} filterParams
   */
  function syncCatalogUrl(filterParams) {
    var next = filterParams.toString();
    var url = window.location.pathname + (next ? '?' + next : '');
    window.history.pushState({ effiloFilters: next }, '', url);
  }

  /**
   * Finds the Effilo infinite product grid on the page.
   * @returns {{ root: Element, list: Element }|null}
   */
  function findProductGrid() {
    var root = document.querySelector('[data-effilo-infinite-grid]');
    if (!root) return null;
    var list = root.querySelector('[data-effilo-infinite-list]');
    if (!list) return null;
    return { root: root, list: list };
  }

  /**
   * Removes rendered product tiles (keeps promo <template> nodes).
   * @param {Element} list
   */
  function clearProductItems(list) {
    Array.prototype.slice.call(list.children).forEach(function (child) {
      if (child.matches && child.matches('li[data-effilo-grid-product]')) {
        child.remove();
        return;
      }
      if (child.matches && child.matches('[data-effilo-grid-promo-item]')) {
        child.remove();
      }
    });
  }

  /**
   * Builds the filtered collection fetch URL for grid items.
   * @param {string} collectionUrl
   * @param {URLSearchParams} filterParams
   * @param {number} page
   * @param {Element} gridRoot
   * @returns {string}
   */
  function buildFilterFetchUrl(collectionUrl, filterParams, page, gridRoot) {
    var params = new URLSearchParams(filterParams.toString());
    params.set('view', FILTER_VIEW);
    params.set('page', String(page || 1));

    var pageSize = gridRoot && gridRoot.dataset.pageSize;
    if (pageSize) params.set('page_size', pageSize);

    var cols = gridRoot && gridRoot.dataset.cols;
    if (cols) params.set('cols', cols);

    return collectionUrl + '?' + params.toString();
  }

  /**
   * Replaces or appends grid products from a filter-grid batch response.
   * @param {string} html
   * @param {{ root: Element, list: Element }} grid
   * @param {boolean} append
   * @returns {HTMLElement|null}
   */
  function applyBatchHtml(html, grid, append) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var batch = doc.getElementById('effilo-filter-grid-batch');
    if (!batch) return null;

    if (!append) {
      clearProductItems(grid.list);
    }

    batch.querySelectorAll('li[data-effilo-grid-product]').forEach(function (li) {
      grid.list.appendChild(li);
    });

    var page = parseInt(batch.dataset.page || '1', 10);
    var pages = parseInt(batch.dataset.pages || '1', 10);
    grid.root.dataset.page = String(page);
    grid.root.dataset.pages = String(pages);
    grid.root.dataset.total = batch.dataset.total || grid.root.dataset.total || '';

    if (!append) {
      grid.root.dataset.effiloInfiniteInit = 'false';
    }

    var sentinel = grid.root.querySelector('[data-effilo-infinite-sentinel]');
    if (sentinel) {
      sentinel.hidden = pages <= page;
      sentinel.classList.remove('is-loading');
    }

    if (!append) {
      document.dispatchEvent(new CustomEvent('effilo:grid:filtered'));
    }

    return batch;
  }

  /**
   * Fetches filtered products and updates the on-page Effilo grid.
   * @param {string} collectionUrl
   * @param {URLSearchParams} filterParams
   * @param {boolean} updateHistory
   * @returns {Promise<void>}
   */
  function refreshProductGrid(collectionUrl, filterParams, updateHistory) {
    var grid = findProductGrid();
    if (!grid) {
      var fallback = collectionUrl + (filterParams.toString() ? '?' + filterParams.toString() : '');
      window.location.href = fallback;
      return Promise.resolve();
    }

    grid.root.classList.add('is-filtering');
    var url = buildFilterFetchUrl(collectionUrl, filterParams, 1, grid.root);

    return fetch(url)
      .then(function (response) {
        return response.text();
      })
      .then(function (html) {
        var batch = applyBatchHtml(html, grid, false);
        if (!batch) {
          window.location.href =
            collectionUrl + (filterParams.toString() ? '?' + filterParams.toString() : '');
          return;
        }

        grid.root.dataset.effiloFilterActive = filterParams.toString() ? 'true' : 'false';

        if (updateHistory !== false) {
          syncCatalogUrl(filterParams);
        }

        // Re-init infinite scroll for the filtered result set.
        if (typeof window.effiloInitInfiniteGrids === 'function') {
          window.effiloInitInfiniteGrids();
        }
      })
      .finally(function () {
        grid.root.classList.remove('is-filtering');
      });
  }

  /**
   * Alpine/data-island controller used by the popular-filters section.
   * @returns {object}
   */
  function popularFiltersController() {
    return {
      collectionUrl: '',

      /**
       * Registers the filters drawer and restores filters from the URL.
       * @returns {void}
       */
      init: function () {
        this.collectionUrl = this.$root.dataset.collectionUrl || '/collections/all';
        if (window.Alpine && Alpine.store('modals')) {
          Alpine.store('modals').register('filters', 'leftDrawer');
        }
        if (typeof initTeleport === 'function') {
          initTeleport(this.$root);
        }

        var pageFilters = getPageFilterParams();
        if (pageFilters.toString()) {
          refreshProductGrid(this.collectionUrl, pageFilters, false);
        }
      },

      /**
       * Applies facet form values to the catalog product grid.
       * @param {Event} e
       * @param {string} [close]
       * @returns {void}
       */
      filterFormSubmit: function (e, close) {
        if (close && window.Alpine && Alpine.store('modals')) {
          Alpine.store('modals').close(close);
        }

        var form = e && e.target;
        if (form && form.tagName && form.tagName.toLowerCase() !== 'form') {
          form = form.form;
        }
        if (!form) return;

        var filterParams = pickFilterParams(new FormData(form));
        refreshProductGrid(this.collectionUrl, filterParams, true);
      },

      /**
       * Clears all filters and restores the unfiltered catalog grid.
       * @param {Event} e
       * @returns {void}
       */
      clearAllFilters: function (e) {
        if (e && e.preventDefault) e.preventDefault();
        if (window.Alpine && Alpine.store('modals')) {
          Alpine.store('modals').close('filters');
        }
        // Full reload restores promo tiles + infinite scroll cleanly.
        window.location.href = window.location.pathname;
      }
    };
  }

  // Expose helpers for the section x-data factory and infinite scroll.
  window.effiloPopularFilters = {
    create: popularFiltersController,
    getPageFilterParams: getPageFilterParams,
    buildFilterFetchUrl: buildFilterFetchUrl,
    applyBatchHtml: applyBatchHtml,
    findProductGrid: findProductGrid,
    isFilterParam: isFilterParam,
    FILTER_VIEW: FILTER_VIEW
  };
})();
